import { CrossplayEventProperties } from '@play-co/gcinstant';
import type { CrossPlayStartParams } from './crossplay.types';
import { isWebIapAvailable } from 'src/lib/iap/iap.util';
import { isPlatformNative } from 'src/lib/native/native.util';
import { GCInstant } from 'src/lib/gcinstant';

/**
 * Detect if the current entry is from the crossplay flow
 */
export function isCrossplayEntry(): boolean {
  const isTargetPlatform = isPlatformOpenWeb() || isPlatformNative();
  return isTargetPlatform && GCInstant.crossplay.isCrossplayEntry();
}

/**
 * Best way to detect if we actually on open web
 */
export function isPlatformOpenWeb(): boolean {
  return isWebIapAvailable();
}

/**
 * Returns true if the game is running in LINE Frontend Framework (LIFF).
 * It dirty way to check this but I don't think we have something better
 */
export function isPlatformLiff(): boolean {
  return !!window.liff?.isInClient();
}

/**
 * Get LINE OA chatbot link
 */
export function getLineChatbotLink(): string {
  return process.env.IS_DEVELOPMENT
    ? 'https://line.me/R/oaMessage/@951fhgqs' // DEV
    : 'https://line.me/R/oaMessage/@buruburudobutsu'; // PROD
}

/**
 * Builds the parameters for a crossplay event.
 *
 * @param args - The starting parameters for the crossplay event.
 * @param install - Optional flag indicating if the event is an install event. Defaults to `false`.
 * @returns The constructed crossplay event properties.
 *
 * @remarks
 * The function includes both `triggerSouce` and `triggerSource` properties to maintain compatibility
 * with existing amplitude charts. The `triggerSouce` property should be removed once it is confirmed
 * that it is no longer in use.
 *
 * @example
 * ```typescript
 * const params: CrossPlayStartParams = { origin: 'game', subFeature: 'feature1' };
 * const eventProps = buildParameters(params, true);
 * console.log(eventProps);
 * ```
 */
export function buildParameters(
  args: CrossPlayStartParams,
  install = false,
): CrossplayEventProperties {
  const origin = args.origin ?? 'unknown';
  const newArgs: CrossplayEventProperties = {
    ...args,
    // Old typo, keeping both for now to not break existing amplitude chart, but we should remove the triggerSouce once we are sure it's not used
    triggerSouce: `${origin}${install ? '_install' : '_redirect'}`,
    triggerSource: `${origin}${install ? '_install' : '_redirect'}`,
    isInstall: install,
  };

  if (args.subFeature) newArgs.$subFeature = args.subFeature;

  delete newArgs.timestamp;
  delete newArgs.origin;

  return newArgs;
}
